<template>
  <div class="x-faq">
    <div v-if="title" class="x-faq__title">
      <h2>{{ title }}</h2>
    </div>
    <div class="x-faq__container">
      <div v-for="(item, index) in faqListData" :key="index" class="x-faq__item">
        <div
          class="x-faq__question"
          :class="{ 'x-faq__question--open': item.isOpen }"
          @click="toggleFaq(index)"
        >
          <Text
            as="h3"
            :variant="titleFontSize"
            :class="{ 'x-faq__question-text--with-dot': showQuestionDot }"
          >
            {{ item.question }}
          </Text>
          <component
            :is="iconComponent"
            class="x-faq__icon"
            :class="{ 'x-faq__icon--rotate': item.isOpen }"
          />
        </div>
        <div class="x-faq__answer" :class="{ 'x-faq__answer--open': item.isOpen }">
          <div class="x-faq__answer-content">
            <div v-if="Array.isArray(item.answer)">
              <div v-for="answer in item.answer">
                <p v-if="!Array.isArray(answer)" v-html="answer"></p>
                <ol v-if="Array.isArray(answer)" class="x-faq__answer--list">
                  <li
                    v-for="(answerItem, answerIndex) in answer"
                    :key="answerIndex"
                    v-html="answerItem"
                  ></li>
                </ol>
              </div>
            </div>
            <p v-else v-html="item.answer" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Text } from '@rendora/design/components/text';
import { useBreakpoint } from '@rendora/design/index';
import IconMore from '~/assets/svg/icon-dropdown.svg?component';

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

const titleFontSize = computed(() => (isMobile.value ? '16' : '24'));

export interface FaqItem {
  question: string;
  answer: string | string[];
  isOpen?: boolean;
  isSlot?: boolean;
}

export interface FaqLink {
  key: string;
  url: string;
}

interface Props {
  // FAQ 数据列表
  faqList: FaqItem[];
  // FAQ 标题
  title?: string;
  // 链接配置
  links?: FaqLink[];
  // 是否显示问题前的圆点
  showQuestionDot?: boolean;
  // 自定义图标组件
  iconComponent?: Component;
  // 最大容器宽度
  maxWidth?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  links: () => [],
  showQuestionDot: true,
  iconComponent: IconMore,
  maxWidth: '1200px',
});

const emit = defineEmits<{
  // 点击联系方式时触发
  contact: [action: string];
  // 切换 FAQ 展开状态时触发
  toggle: [index: number, isOpen: boolean];
}>();

// 内部 FAQ 数据，添加 isOpen 状态
const faqListData = ref<Required<FaqItem>[]>(
  props.faqList.map(item => ({
    ...item,
    isOpen: item.isOpen ?? false,
  })),
);

// 切换 FAQ 展开/收起
const toggleFaq = (index: number) => {
  faqListData.value[index].isOpen = !faqListData.value[index].isOpen;
  emit('toggle', index, faqListData.value[index].isOpen);
};

// 处理点击事件，支持自定义链接和联系方式
const handleClick = (event: Event) => {
  const target = event.target as HTMLElement;
  if (target.tagName === 'A' && target.dataset.action) {
    event.preventDefault();
    emit('contact', target.dataset.action);
  }
};

// 监听页面点击事件
onMounted(() => {
  document.addEventListener('click', handleClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClick);
});

// 监听 props.faqList 变化，更新内部数据
watch(
  () => props.faqList,
  newList => {
    faqListData.value = newList.map(item => ({
      ...item,
      isOpen: item.isOpen ?? false,
    }));
  },
  { deep: true },
);
</script>

<style lang="scss" scoped src="./styles/pc.scss" />
<style lang="scss" scoped src="./styles/mobile.scss" />
